<template>
  <v-card class="mb-4 filter-panel">
    <v-card-title class="py-3">
      <v-icon class="mr-2">
        {{ icons.mdiFilterVariant }}
      </v-icon>
      絞り込み
    </v-card-title>

    <v-card-text class="filter-panel__content">
      <div class="text-caption grey--text mb-4">
        来店回数・売上・RFMセグメントは直近3ヶ月のデータで集計されます
      </div>

      <!-- RFMセグメントフィルター -->
      <div class="mb-4">
        <div class="text-subtitle-2 mb-2">
          RFMセグメント
        </div>
        <v-chip-group
          v-model="filters.rfmSegment"
          column
          multiple
        >
          <v-tooltip
            v-for="(label, key) in SEGMENT_LABELS"
            :key="key"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-chip
                :value="key"
                :color="SEGMENT_COLORS[key]"
                filter
                outlined
                v-bind="attrs"
                v-on="on"
              >
                {{ label }}
              </v-chip>
            </template>
            <span>{{ SEGMENT_DESCRIPTIONS[key] }}</span>
          </v-tooltip>
        </v-chip-group>
      </div>

      <!-- 来店回数フィルター -->
      <div class="mb-4">
        <v-text-field
          v-model.number="filters.minVisitCount"
          label="来店回数"
          type="number"
          suffix="回以上"
          min="0"
          dense
          outlined
          hide-details
        />
        <div
          v-if="filters.minVisitCount"
          class="mt-2"
        >
          <div class="text-caption grey--text mb-1">
            集計期間（未指定で直近3ヶ月）
          </div>
          <v-row dense>
            <v-col cols="6">
              <v-menu
                v-model="visitCountStartDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="filters.visitCountStartDate"
                    label="開始日"
                    :prepend-inner-icon="icons.mdiCalendar"
                    readonly
                    dense
                    outlined
                    hide-details
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filters.visitCountStartDate = null"
                  />
                </template>
                <v-date-picker
                  v-model="filters.visitCountStartDate"
                  no-title
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  @input="visitCountStartDateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu
                v-model="visitCountEndDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="filters.visitCountEndDate"
                    label="終了日"
                    :prepend-inner-icon="icons.mdiCalendar"
                    readonly
                    dense
                    outlined
                    hide-details
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filters.visitCountEndDate = null"
                  />
                </template>
                <v-date-picker
                  v-model="filters.visitCountEndDate"
                  no-title
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  @input="visitCountEndDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- 最終来店日フィルター -->
      <div class="mb-4">
        <div class="text-subtitle-2 mb-2">
          最終来店日
        </div>
        <v-row dense>
          <v-col cols="6">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.lastVisitAfter"
                  label="開始日"
                  :prepend-inner-icon="icons.mdiCalendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="filters.lastVisitAfter = null"
                />
              </template>
              <v-date-picker
                v-model="filters.lastVisitAfter"
                no-title
                locale="ja"
                :day-format="date => new Date(date).getDate()"
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.lastVisitBefore"
                  label="終了日"
                  :prepend-inner-icon="icons.mdiCalendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="filters.lastVisitBefore = null"
                />
              </template>
              <v-date-picker
                v-model="filters.lastVisitBefore"
                no-title
                locale="ja"
                :day-format="date => new Date(date).getDate()"
                @input="endDateMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </div>

      <!-- 誕生日フィルター（月選択） -->
      <div class="mb-4">
        <v-select
          v-model="filters.birthdayMonth"
          :items="birthdayMonthOptions"
          item-text="text"
          item-value="value"
          label="誕生日"
          dense
          outlined
          hide-details
          clearable
        />
      </div>

      <!-- 関連キャストフィルター -->
      <div class="mb-4">
        <v-select
          v-model="filters.castIds"
          :items="castUsers"
          item-text="name"
          item-value="id"
          label="関連キャスト"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          outlined
          hide-details
          clearable
        />
      </div>

      <!-- 顧客タグフィルター -->
      <div class="mb-4">
        <v-select
          v-model="filters.customerTagIds"
          :items="customerTags"
          item-text="name"
          item-value="id"
          label="顧客タグ"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          outlined
          hide-details
          clearable
        />
        <!-- タグ名インライン編集 -->
        <div
          v-if="customerTags.length > 0"
          class="mt-2"
        >
          <div class="text-caption grey--text mb-1">
            タグ名を編集（クリックで編集）
          </div>
          <div class="tag-edit-list">
            <div
              v-for="tag in customerTags"
              :key="tag.id"
              class="d-flex align-center mb-1"
            >
              <template v-if="editingTagId === tag.id">
                <v-text-field
                  v-model="editingTagName"
                  dense
                  hide-details
                  outlined
                  class="mr-1"
                  style="max-width: 180px;"
                  @keyup.enter="saveTagName(tag)"
                  @keyup.esc="cancelTagEdit"
                />
                <v-btn
                  icon
                  x-small
                  color="primary"
                  @click="saveTagName(tag)"
                >
                  <v-icon x-small>
                    {{ icons.mdiCheck }}
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  @click="cancelTagEdit"
                >
                  <v-icon x-small>
                    {{ icons.mdiClose }}
                  </v-icon>
                </v-btn>
              </template>
              <template v-else>
                <v-chip
                  small
                  outlined
                  class="cursor-pointer"
                  @click="startTagEdit(tag)"
                >
                  {{ tag.name }}
                  <v-icon
                    x-small
                    class="ml-1"
                  >
                    {{ icons.mdiPencil }}
                  </v-icon>
                </v-chip>
              </template>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        text
        @click="handleReset"
      >
        リセット
      </v-btn>
      <v-btn
        color="primary"
        @click="handleApply"
      >
        <v-icon left>
          {{ icons.mdiMagnify }}
        </v-icon>
        適用
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  reactive, ref, onMounted, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiFilterVariant, mdiCalendar, mdiMagnify, mdiPencil, mdiCheck, mdiClose,
} from '@mdi/js'
import { SEGMENT_LABELS, SEGMENT_DESCRIPTIONS, SEGMENT_COLORS } from '../composables/useRfmAnalysis'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import UserApi from '@/api/waiter/User'

const getDefaultFilters = () => ({
  rfmSegment: [],
  minVisitCount: null,
  visitCountStartDate: null,
  visitCountEndDate: null,
  lastVisitAfter: null,
  lastVisitBefore: null,
  customerTagIds: [],
  castIds: [],
  birthdayMonth: null,
})

export default {
  name: 'CustomerFilterPanel',
  props: {
    customerTags: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const filters = reactive(getDefaultFilters())
    const startDateMenu = ref(false)
    const endDateMenu = ref(false)
    const visitCountStartDateMenu = ref(false)
    const visitCountEndDateMenu = ref(false)

    // タグ名インライン編集
    const editingTagId = ref(null)
    const editingTagName = ref('')

    const startTagEdit = tag => {
      editingTagId.value = tag.id
      editingTagName.value = tag.name
    }

    const cancelTagEdit = () => {
      editingTagId.value = null
      editingTagName.value = ''
    }

    const saveTagName = async tag => {
      const newName = editingTagName.value.trim()
      if (!newName || newName === tag.name) {
        cancelTagEdit()

        return
      }
      try {
        await CustomerTagApi.updateCustomerTag({ id: tag.id, name: newName })
        emit('tag-updated', { id: tag.id, name: newName })
        vm.$toast.success('タグ名を更新しました')
      } catch (e) {
        vm.$toast.error('タグ名の更新に失敗しました')
      }
      cancelTagEdit()
    }

    const castUsers = ref([])

    const fetchCastUsers = async () => {
      try {
        const res = await UserApi.getUsers('cast')
        if (res.data?.users?.data) {
          castUsers.value = res.data.users.data.map(u => ({
            id: Number(u.id),
            name: u.attributes.name,
          }))
        }
      } catch (e) {
        console.error('Failed to fetch cast users:', e)
      }
    }

    onMounted(() => {
      fetchCastUsers()
    })

    const birthdayMonthOptions = Array.from({ length: 12 }, (_, i) => ({
      text: `${i + 1}月`,
      value: i + 1,
    }))

    const handleApply = () => {
      emit('apply', {
        rfmSegment: filters.rfmSegment,
        minVisitCount: filters.minVisitCount,
        visitCountStartDate: filters.visitCountStartDate,
        visitCountEndDate: filters.visitCountEndDate,
        lastVisitAfter: filters.lastVisitAfter,
        lastVisitBefore: filters.lastVisitBefore,
        customerTagIds: filters.customerTagIds,
        castIds: filters.castIds,
        birthdayMonth: filters.birthdayMonth,
      })
    }

    const handleReset = () => {
      const defaults = getDefaultFilters()
      Object.keys(defaults).forEach(key => {
        filters[key] = defaults[key]
      })
      emit('reset')
    }

    return {
      filters,
      startDateMenu,
      endDateMenu,
      visitCountStartDateMenu,
      visitCountEndDateMenu,
      castUsers,
      birthdayMonthOptions,
      editingTagId,
      editingTagName,
      startTagEdit,
      cancelTagEdit,
      saveTagName,
      handleApply,
      handleReset,
      SEGMENT_LABELS,
      SEGMENT_DESCRIPTIONS,
      SEGMENT_COLORS,
      icons: {
        mdiFilterVariant,
        mdiCalendar,
        mdiMagnify,
        mdiPencil,
        mdiCheck,
        mdiClose,
      },
    }
  },
}
</script>

<style scoped>
.filter-panel {
  position: sticky;
  top: 64px;
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
}
.filter-panel__content {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.tag-edit-list {
  max-height: 200px;
  overflow-y: auto;
}
</style>
